import React from 'react';
import camelCase from 'lodash/camelCase';

// Helpers
import * as ReviewFormTemplatesHelpers from 'Shared/model_helpers/ReviewFormTemplates.helper';

// Utils
import { generateHeaderForQuestion } from 'Shared/utils/Reviews.utils';
import { isEmpty } from 'Wpds/components/rich-text/utils/EditorState.utils';

// Components
import { WpRichTextInput } from 'Wpds/components/rich-text';
import { WpBadgeList, WpBadge } from 'Wpds/components/badge';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import ReviewRatingChart from '../ReviewRatingChart/ReviewRatingChart';
import PrivateQuestionPopover from './PrivateQuestionPopover';
import QuestionLeftUnanswered from './QuestionLeftUnanswered';

// Styles
import './RatingQuestionReview.scss';

type Props = {
  question;
  answers;
  formData?;
  alternateKey?: string;
  slimVersion?: boolean;
  isSummaryQuestion?: boolean;
};

const RatingQuestionReview = ({
  question,
  answers,
  formData,
  alternateKey,
  slimVersion = false,
  isSummaryQuestion = false,
}: Props) => {
  const questionKey = ReviewFormTemplatesHelpers.getQuestionKey(question);
  const header = generateHeaderForQuestion({ question });
  const values = ReviewFormTemplatesHelpers.getValues(question).toJS();
  const showBadge = ReviewFormTemplatesHelpers.getQuestionPrivate(question);

  const key = alternateKey || questionKey;

  // the schema normalizer converts all object keys to camel case,
  // including the question keys. if the snake case key doesn't return results
  // check to see if the key is camel case.
  const answersToQuestions = answers[key] || answers[camelCase(key)];
  const rating = answersToQuestions?.rating;
  const summary = answersToQuestions?.explanation;

  const isAnswerEmpty = !rating && isEmpty(summary);

  const options = values.map(value => {
    return {
      value: value.number.toString(),
      label: value.number.toString(),
      tooltip: value.label,
    };
  });

  const HorizontalBarChartForSummary = () => {
    const groupAverages = formData?.toJS()?.[camelCase(questionKey)]?.groupAverages || {};
    const groups = Object.keys(groupAverages);
    const groupToLabel = {
      receiver: 'Self Score: ',
      compiler: 'Final Score: ',
    };
    const bars = groups
      .filter(group => groupToLabel[group])
      .map(group => ({ ...groupAverages[group], label: groupToLabel[group] }));
    return <ReviewRatingChart groups={bars} options={options} />;
  };

  const { tooltip } = options.find(option => option.value === rating) || {};

  const showScale = !slimVersion;
  const baseClassName = 'RatingQuestionReview';
  return (
    <div className={baseClassName}>
      {!slimVersion && (
        <div className={`${baseClassName}__property`}>
          {header}
          {showBadge && <PrivateQuestionPopover />}
        </div>
      )}
      {!isAnswerEmpty ? (
        <>
          {isSummaryQuestion && <HorizontalBarChartForSummary />}
          {showScale && !isSummaryQuestion && (
            <div className={`${baseClassName}__badges`}>
              <WpBadgeList>
                {options.map(option => {
                  const isActive = rating === option.value;
                  return (
                    <WpBadge
                      color={isActive ? 'purple' : 'gray'}
                      size="lg"
                      key={`${questionKey}-rating-${option.value}`}
                    >
                      {option.label}
                    </WpBadge>
                  );
                })}
              </WpBadgeList>
            </div>
          )}
          {rating && !isSummaryQuestion && (
            <WpContainer className={`${baseClassName}__rating-label`}>
              <WpContainerBody padding="05">
                <div className={`${baseClassName}__rating-label__inner`}>
                  <WpBadge color="purple" size="lg">
                    {rating}
                  </WpBadge>
                  {tooltip && <div className={`${baseClassName}__rating-label__label`}>{tooltip}</div>}
                </div>
              </WpContainerBody>
            </WpContainer>
          )}
          <div className={`${baseClassName}__text`}>
            <WpRichTextInput
              value={summary}
              readOnly
              options={{
                showBorder: false,
                transparent: true,
                overflowBehavior: 'grow',
                backgroundColor: 'transparent',
              }}
              height="0"
            />
          </div>
        </>
      ) : (
        <QuestionLeftUnanswered />
      )}
    </div>
  );
};

export default RatingQuestionReview;
